<template>
  <nav class="header-navbar navbar navbar-expand-lg navbar-dark fixed-top">
    <div class="container dropdown">
      <router-link class="navbar-brand" :to="{ name: `home-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.title') }}</router-link>

      <button class="navbar-toggler menu-button" :class="{ collapsed: !isOpenMenu }" @click="toggleMenu" type="button">
        <span class="menu-closed d-print-none">{{ $t('close') }}</span>
        <span class="menu-open d-print-none">{{ $t('menu') }}</span>
        <span class="burger-to-x-icon" />
      </button>

      <BCollapse id="menu" :visible="isOpenMenu" class="navbar-collapse">
        <div class="collapse-inner">
          <ul class="navbar-nav me-auto" name="login-menu">
            <li class="nav-item d-inline-block d-lg-none d-xl-none no-border">
              <LocalePicker inline />
            </li>
            <template v-if="isLoggedIn">
              <li class="nav-item d-inline-block d-lg-none d-xl-none no-border login-menu logged-in">
                <span class="icon-user fw-bold">{{ profile.name }}</span>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <router-link class="nav-link" :to="{ name: `profile-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">
                  {{ $t('navbar.profile.details') }}
                </router-link>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <router-link class="nav-link" :to="{ name: `purchaseHistory-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">
                  {{ $t('navbar.profile.purchaseHistory') }}
                </router-link>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <router-link class="nav-link" :to="{ name: `myNotifications-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">
                  {{ $t('navbar.profile.myNotifications') }}
                  <span v-if="profile.unreadNotificationCount">
                    <span class="badge badge-danger">{{ profile.unreadNotificationCount }}</span>
                  </span>
                </router-link>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <router-link class="nav-link" :to="{ name: `myConsents-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">
                  {{ $t('navbar.profile.myConsents') }}
                </router-link>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <router-link class="nav-link" :to="{ name: `myConsentRequests-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">
                  {{ $t('navbar.profile.myConsentRequests') }}
                </router-link>
              </li>

              <li class="nav-item d-inline-block d-lg-none d-xl-none">
                <a class="nav-link" href="#" name="logout" @click.prevent="logout">
                  {{ $t('navbar.profile.logout') }}
                  <span class="icon-enter float-end" />
                </a>
              </li>
            </template>
            <li class="nav-item d-inline-block d-lg-none d-xl-none no-border" v-else>
              <router-link
                class="nav-link icon-user fw-bold text-uppercase"
                :to="{ name: `login-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }"
              >{{ $t('navbar.login') }}</router-link>
            </li>

            <li class="nav-item d-inline-block d-lg-none d-xl-none no-border">
              <span class="dropdown-item d-inline-block d-lg-none d-xl-none navbar-subheader">
                {{ $t('menu') }}
              </span>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" :to="{ name: `home-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.home') }}</router-link>
            </li>
            <li class="nav-item with-dropdown" :class="{ show: isVisible.menu }">
              <BDropdown
                ref="menu"
                toggle-class="nav-link"
                menu-class="navigation-dropdown full-screen"
                variant="link"
                no-flip
                :popperOpts="ignorePopperStyles"
                boundary="window"
              >
                <template v-slot:button-content>
                  {{ $t('navbar.services') }}
                  <span class="icon-arrow-down32 d-none d-lg-inline-block" />
                  <span class="icon-arrow-end32 d-inline-block d-lg-none d-xl-none" />
                </template>
                <template v-slot:default>
                  <BDropdownItem
                    class="d-inline-block d-lg-none d-xl-none btn-secondary btn-md btn icon-arrow-left32"
                  >{{ $t('navbar.back') }}</BDropdownItem>
                  <span class="dropdown-item d-inline-block d-lg-none d-xl-none navbar-subheader">
                    {{ $t('navbar.services') }}
                  </span>
                  <div class="nav-group" v-for="groupCode in groupCodes" :key="groupCode">
                    <BDropdownItem
                      v-for="product in groupedProducts[groupCode]"
                      :key="product.id"
                      :to="product.path ? product.path : { name: `report-${this.$i18n.locale}`, params: { ...product, lang: this.$i18n.locale } }"
                    >
                      {{ product.title }}
                      <span class="icon-double-arrow-end" v-if="product.external" />
                    </BDropdownItem>
                  </div>
                </template>
              </BDropdown>
            </li>

            <li class="nav-item">
              <router-link :to="{ name: `prices-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }" class="nav-link">{{ $t('navbar.prices') }}</router-link>
            </li>

            <li class="nav-item">
              <router-link :to="{ name: `faq-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }" class="nav-link">{{ $t('footer.menu.faq') }}</router-link>
            </li>
          </ul>

          <ul class="navbar-nav right-list ms-auto">
            <li class="nav-item d-none d-lg-inline-block no-background">
              <LocalePicker />
            </li>
            <li v-if="isLoggedIn" class="nav-item d-none d-xl-inline-block navbar-notification-icon">
              <router-link :to="{ name: `myNotifications-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }" style="text-decoration: none">
                <router-link :to="{ name: `myNotifications-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }" class="icon icon-envelope2 d-inline" />
                <span v-if="profile.unreadNotificationCount > 0" class="badge">{{ profile.unreadNotificationCount }}</span>
              </router-link>
            </li>
            <li
              name="login-menu"
              class="nav-item d-none d-lg-inline-block login-menu"
              :class="{ 'logged-in': isLoggedIn, show: isVisible.profile }"
            >
              <BDropdown
                toggle-class="icon-user nav-link"
                menu-class="navigation-dropdown"
                variant="none"
                ref="profile"
                :popperOpts="ignorePopperStyles"
                v-if="isLoggedIn"
              >
                <template v-slot:button-content
                >{{ profile.name }} <span class="icon-arrow-down32" /></template>
                <template v-slot:default>
                  <div class="nav-group">
                    <BDropdownItem :to="{ name: `profile-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.profile.details') }}</BDropdownItem>
                    <BDropdownItem :to="{ name: `myNotifications-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.profile.myNotifications') }}</BDropdownItem>
                    <BDropdownItem :to="{ name: `myConsents-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.profile.myConsents') }}</BDropdownItem>
                    <BDropdownItem :to="{ name: `myConsentRequests-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.profile.myConsentRequests') }}</BDropdownItem>
                    <BDropdownItem :to="{ name: `purchaseHistory-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }">{{ $t('navbar.profile.purchaseHistory') }}</BDropdownItem>
                    <BDropdownItem name="logout" @click.prevent="logout">
                      {{ $t('navbar.profile.logout') }} <span class="icon-enter float-end" />
                    </BDropdownItem>
                  </div>
                </template>
              </BDropdown>
              <router-link
                class="nav-link icon-user"
                :to="{ name: `login-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } }"
                v-else
              >{{ $t('navbar.login') }}</router-link>
            </li>
          </ul>
        </div>
      </BCollapse>
    </div>
  </nav>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import LocalePicker from './LocalePicker';
import { BCollapse, BDropdown, BDropdownItem } from 'bootstrap-vue-next';

export default {
  components: {
    LocalePicker,
    BDropdown,
    BDropdownItem,
    BCollapse
  },
  data: () => ({
    isOpenMenu: false,
    isVisible: {},
    ignorePopperStyles: {
      modifiers: {
        applyStyle: {
          enabled: false,
        },
      },
    },
  }),
  computed: {
    ...mapState('product', ['groups']),
    ...mapGetters('product', ['groupCodes', 'groupedProducts']),
    ...mapGetters('session', ['isLoggedIn']),
    ...mapState('session', ['profile']),
  },
  watch: {
    $route: 'closeMenu',
  },
  methods: {
    ...mapActions('session', ['logout']),
    watchDropdownVisibility(refName) {
      this.$watch(
        () => ({
          logged: this.isLoggedIn,
          value: !!this.$refs[refName] && this.$refs[refName].visible,
        }),
        ({ value }) => {
          this.isVisible[refName] = value;
        },
      );
    },
    closeMenu() {
      this.isOpenMenu = false;
    },
    toggleMenu() {
      this.isOpenMenu = !this.isOpenMenu;
    },
  },
  mounted() {
    ['menu', 'profile'].forEach((name) => this.watchDropdownVisibility(name));
  },
};
</script>
